import styled from 'styled-components'
import { Button, ButtonGroup, PersonIcon, Wrapper } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { useFormikContext } from 'formik'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'

import useApiHelpers from 'lib/effects/api-helpers'
import useAdminUserId from 'hooks/useAdminUserId'
import { formatAdminUserOptions } from 'utils/helpers'
import { AdminUser } from 'lib/models/admin-user'
import { Task, TaskAttributes } from 'lib/models/task'

import SelectInput from 'components/form/select-input'
import AdminIcon from 'components/admin-icon'
import Label from 'components/styled/label'

import { useTasksContext } from './context'
import Dropdown from 'components/dropdown'
import { TASK_ASSIGNMENT_ROLES } from './constants'
import ENV from 'config/environment'

const StyledAssignTaskButton = styled(Button.Secondary)`
  && {
    padding: ${GTR.XS} ${GTR.L};
  }
`

const StyledRemoveAssigneeButton = styled(Button.Underline)`
  font-size: ${FONT.SIZE.S};
`

const StyledAdminIcon = styled(AdminIcon)`
  margin-right: 0;
`

const StyledTakeItButton = styled(Button)`
  font-size: ${FONT.SIZE.S};
`

type TaskAssignmentFormValues = {
  currentAssigneeId: TaskAttributes['currentAssigneeId']
}

type TaskAssignmentProps = {
  task: Task
  fetchEventsForTask: () => boolean | void
  refetchTasks?: () => void
}

const TaskAssignment = ({
  task,
  fetchEventsForTask,
  refetchTasks = () => {
    return
  },
}: TaskAssignmentProps) => {
  const { values } = useFormikContext<TaskAssignmentFormValues>()
  const cache = useQueryClient()
  const adminUsers = cache.getQueryData<AdminUser[]>('adminUsers')
  const probateCaseAdmins = ENV.PROBATE_OWNERS_GROUP
    ? adminUsers?.filter((u) =>
        u.attributes.microsoftGroups.includes(ENV.PROBATE_OWNERS_GROUP)
      )
    : adminUsers
  const { updateTask } = useTasksContext()
  const adminUserId = useAdminUserId()
  const hasAssignee = task.attributes.currentAssignee
  const showAssignButton =
    task.attributes.currentAssigneeId !== values.currentAssigneeId
  const showTakeItButton = task.attributes.currentAssigneeId !== adminUserId
  const adminUserOptions = formatAdminUserOptions(probateCaseAdmins)

  const {
    createItem: createAssignment,
    isCreating: isCreatingAssignment,
    deleteItem: deleteTaskAssignment,
    isDeleting: isDeletingTaskAssignment,
  } = useApiHelpers({
    baseUrl: `/api/task-assignments`,
    type: 'task_assignments',
  })

  const handleCreateClick = async ({
    adminUserId,
  }: { adminUserId?: number } = {}) => {
    await createAssignment({
      adminUserId: adminUserId || values.currentAssigneeId,
      role: TASK_ASSIGNMENT_ROLES.ASSIGNED,
      taskId: task.id,
    })
    await updateTask(task.id, {})
    await fetchEventsForTask()
    refetchTasks()
    toast('Task assigned.', {
      toastId: 'task-assigned',
    })
  }

  const handleDeleteClick = async () => {
    await deleteTaskAssignment(task.attributes.currentAssignmentId)
    await updateTask(task.id, {})
    await fetchEventsForTask()
    refetchTasks()
    toast('Assignment removed.', {
      toastId: 'assignment-removed',
    })
  }

  return (
    <Dropdown
      updatedAt={task.attributes.updatedAt}
      trigger={
        hasAssignee ? (
          <StyledAdminIcon
            user={task.attributes.currentAssignee}
            enableTooltip
          />
        ) : (
          <PersonIcon color={COLOR.WHITE} background={COLOR.GREY.LIGHT} />
        )
      }
      content={
        <Wrapper padding="S">
          <Label $error={false}>
            Select task owner{' '}
            {showTakeItButton && (
              <>
                or{' '}
                <StyledTakeItButton
                  type="button"
                  onClick={() => handleCreateClick({ adminUserId })}
                >
                  take it
                </StyledTakeItButton>
              </>
            )}
          </Label>
          <SelectInput
            name="currentAssigneeId"
            options={adminUserOptions}
            small
          />
          <ButtonGroup margin={['S', 0, 0]}>
            {showAssignButton && (
              <StyledAssignTaskButton
                type="button"
                onClick={handleCreateClick}
                loading={isCreatingAssignment}
              >
                Assign task
              </StyledAssignTaskButton>
            )}
            {hasAssignee && (
              <StyledRemoveAssigneeButton
                type="button"
                onClick={handleDeleteClick}
                loading={isDeletingTaskAssignment}
              >
                Remove assignee
              </StyledRemoveAssigneeButton>
            )}
          </ButtonGroup>
        </Wrapper>
      }
    />
  )
}

export default TaskAssignment
